import { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  CreditCard,
  User,
  Envelope,
  Info,
  Heart,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { api } from "./api";

// ─── Google icon (inline SVG — not a Phosphor icon) ─────────────────────────
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// ─── Card definitions ────────────────────────────────────────────────────────
const CARDS = [
  {
    Icon: Lock,
    bg: "bg-[#FFF3EE]",
    iconCls: "text-[#FF7A45]",
    title: "Your Privacy Matters",
    desc: "We never sell your personal data or use your child's information for third-party advertising. We share information with trusted service providers only when needed to run Bloom.",
  },
  {
    Icon: null,
    bg: "bg-blue-50",
    iconCls: "",
    title: "Google Sign-In",
    desc: "Sign in securely with your Google account — no separate Bloom password needed. Google shares your name and email with us to set up your account.",
  },
  {
    Icon: CreditCard,
    bg: "bg-green-50",
    iconCls: "text-green-600",
    title: "Secure Payments",
    desc: "Payments are handled securely by Stripe. We don't store your full payment card details on Bloom's servers.",
  },
  {
    Icon: User,
    bg: "bg-purple-50",
    iconCls: "text-purple-600",
    title: "Your Child's Profile",
    desc: "You choose your child's profile name — a nickname is fine! We don't require their real name.",
  },
  {
    Icon: Envelope,
    bg: "bg-teal-50",
    iconCls: "text-teal-600",
    title: "Stay in Touch",
    desc: "With your permission, we may send you useful tips and updates about Bloom. You can unsubscribe from marketing emails at any time.",
  },
  {
    Icon: Info,
    bg: "bg-pink-50",
    iconCls: "text-pink-600",
    title: "Your Rights",
    desc: "You can manage your account and delete child profiles through the available settings. For help with your data or account deletion, email team@webuilthere.com.",
  },
  {
    Icon: Heart,
    bg: "bg-[#FFF3EE]",
    iconCls: "text-[#FF7A45]",
    title: "Our Promise",
    desc: "Bloom was built for families. We're committed to protecting your privacy, keeping your information secure and being transparent about how we use it.",
  },
];

// ─── TermsSheet ──────────────────────────────────────────────────────────────
// Shown by RequireAuth when user.accepted_terms_at === null.
// onAccepted should be refresh() from AuthContext — re-fetches /auth/me so the
// accepted_terms_at field is populated and RequireAuth's guard clears.
//
// Backend: POST /auth/accept-terms  (see auth.py)
export function TermsSheet({ onAccepted }) {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signingOut, setSigningOut] = useState(false);

  const handleAccept = async () => {
    if (!agreed || loading) return;
    setLoading(true);
    setError("");
    try {
      await api("/auth/accept-terms", { method: "POST" });
      onAccepted(); // triggers refresh() → re-fetches /auth/me → gate clears
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      // Even if the logout call fails, redirect — the session will expire
    }
    window.location.href = "/vocab";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/30 backdrop-blur-[2px] sm:items-center">
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        className="relative flex max-h-[92svh] w-full max-w-md flex-col rounded-t-[2rem] bg-background shadow-soft-lg sm:rounded-[2rem]"
      >
        {/* Drag handle — mobile only */}
        <div className="flex justify-center pb-1 pt-3 sm:hidden" aria-hidden="true">
          <span className="h-1 w-10 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-3.5 border-b border-border px-5 py-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF3EE]">
            <ShieldCheck size={24} weight="fill" className="text-[#FF7A45]" />
          </div>
          <div>
            <h2 className="font-display text-[17px] font-black leading-tight text-foreground">
              Before you explore Bloom
            </h2>
            <p className="text-xs font-semibold text-muted-foreground">
              Simple, transparent, and built for families.
            </p>
          </div>
        </div>

        {/* Scrollable cards */}
        <div className="flex-1 space-y-2.5 overflow-y-auto px-4 py-4">
          {CARDS.map(({ Icon, bg, iconCls, title, desc }) => (
            <div
              key={title}
              className="flex gap-3.5 rounded-2xl border border-border bg-card p-3.5"
            >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${bg}`}
              >
                {Icon ? (
                  <Icon size={20} weight="fill" className={iconCls} />
                ) : (
                  <GoogleIcon />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-display text-sm font-black text-foreground">{title}</p>
                <p className="mt-0.5 text-xs font-semibold leading-relaxed text-muted-foreground">
                  {desc}
                </p>
              </div>
            </div>
          ))}

          <p className="py-1 text-center text-xs font-semibold text-muted-foreground">
            <a
              href="/vocab/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
            >
              Full Privacy Policy
            </a>
            {" · "}
            <a
              href="/vocab/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
            >
              Full Terms
            </a>
          </p>
        </div>

        {/* Footer — checkbox + button */}
        <div className="border-t border-border px-5 pb-6 pt-4">
          {error && (
            <p className="mb-3 text-xs font-bold text-destructive" role="alert">
              {error}
            </p>
          )}

          {/* Agreement checkbox */}
          <button
            type="button"
            onClick={() => setAgreed((v) => !v)}
            className="mb-4 flex w-full items-start gap-3 text-left"
            aria-pressed={agreed}
          >
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors duration-150 ${
                agreed
                  ? "border-[#FF7A45] bg-[#FF7A45]"
                  : "border-border bg-background"
              }`}
              aria-hidden="true"
            >
              {agreed && (
                <svg viewBox="0 0 12 10" width="12" height="10" fill="none">
                  <path
                    d="M1 5L4 8.5L11 1"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className="text-sm font-semibold leading-snug text-foreground">
              I agree to Bloom's{" "}
              <a
                href="/vocab/terms"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
              >
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a
                href="/vocab/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
              >
                Privacy Policy
              </a>
            </span>
          </button>

          <BloomButton fullWidth onClick={handleAccept} disabled={!agreed || loading}>
            {loading ? "Saving…" : "Accept & Continue"}
          </BloomButton>

          {/* Sign out escape hatch */}
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="mt-3 w-full text-center text-xs font-semibold text-muted-foreground transition hover:text-foreground disabled:opacity-50"
          >
            {signingOut ? "Signing out…" : "Not ready? Sign out"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}